import * as React from "react";
import { CommonHelper } from "../../../../../react-core/Helpers/CommonHelper";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../react-core/Html/TextBoxEditor";
import { LeiCode } from "../../../../../react-core/Html/Validation/RegExPatterns";
import { MifirCountryIdentifierClient } from "../../../../../Wrap/Api/Regulations/MifirCountryIdentifier";
import { CountryCode } from "../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { MifirIdentifierType } from "../../../../../Wrap/Constants/Enums/Domain/MifirIdentifierType";
import { InvestorType } from "../../../../../Wrap/Models/Domains/Account/SlAccounts/InvestorType";
import { NationalIdentifier } from "../../../../../Wrap/Models/Domains/Personal/NationalIdentifier";
import { MifirCountryIdentifier } from "../../../../../Wrap/Models/Domains/Regulations/MifirCountryIdentifier";
import { TransferBeneficalOwner } from "../../../../../Wrap/Models/Domains/Transfer/TransferBeneficalOwner";
import { CustomerTaxResidencyModel } from "../../../../../Wrap/Models/Models/Customer/Regulatory/CustomerTaxResidencyModel";
import { NINumberTextEditor } from "../../../../Dashboard/Common/NINumberTextEditor";
import { NationalIdentifierPair } from "../../../../Dashboard/Components/Clients/NewBusinessWizard/Components/FullClientDetail/NationalIdentifierPair";
import { CountryCodeMap } from "../../../../Shared/Entities/Maps/CountryCodeMap";
import { IProcessComponentProps, ProcessComponent } from "../../../../Shared/Process/Process/ProcessComponent";
import { TransferInstructionQuery } from "../../Resources/TransferInstructionQuery";

interface IChangeOfBeneficialOwnerAccountProps extends IProcessComponentProps {
    data: TransferInstructionQuery;
    onChange: (key: string, value: any, data?: any) => void;
    sourceAccount: TransferBeneficalOwner;
    destinationAccount: TransferBeneficalOwner;
}

interface IChangeOfBeneficialOwnerAccountState {
    mifirCountryIdentifiers: Map<CountryCode, MifirCountryIdentifier[]>;
}

const enum accountDirection {
    source,
    destination,
}

export class ChangeOfBeneficialOwnerAccount extends ProcessComponent<
    IChangeOfBeneficialOwnerAccountProps,
    IChangeOfBeneficialOwnerAccountState
> {
    constructor(props: IChangeOfBeneficialOwnerAccountProps) {
        super(props);
        this.state = {
            mifirCountryIdentifiers: new Map<CountryCode, MifirCountryIdentifier[]>(),
        };
    }

    private onChange(changes) {
        const { ChangeOfBeneficialOwner } = this.props.data;
        for (const key in changes) {
            ChangeOfBeneficialOwner[key] = changes[key];
        }
        this.props.onChange("ChangeOfBeneficialOwner", ChangeOfBeneficialOwner);
    }

    private renderAccount(
        account: TransferBeneficalOwner,
        direction: accountDirection,
        primary: NationalIdentifier,
        secondary: NationalIdentifier,
        sourceLei: string,
        destinationLei: string
    ) {
        if (!account) {return null;}
        const label = direction == accountDirection.source ? "Source Account" : "Destination Account";
        //Trust display LEI code
        if (account?.ClientAccount?.InvestorType === InvestorType.Trust) {
            return (account?.BeneficialOwners || []).map((ele) => {
                const data = direction == accountDirection.source ? sourceLei : destinationLei;
                return (
                    <TextBoxEditor
                        label={`${label} LEI `}
                        value={ele.LEICode || data}
                        maxLength={20}
                        cssWrapper="control-size-430"
                        disabled={!CommonHelper.isNullOrEmpty(ele.LEICode)}
                        regex={{ errorMessage: "LEI code must contain 20 characters", value: LeiCode }}
                        onChange={(v) =>
                            this.onChange({
                                [direction == accountDirection.source
                                    ? "CustomSourceTrustLei"
                                    : "CustomDestinationTrustLei"]: v,
                            })
                        }
                    />
                );
            });
        }
        let result = [];
        // individual or joint account
        const beneficialOwners = (account?.BeneficialOwners || [])
            .filter((owner) => owner.isPrimary)
            .concat((account?.BeneficialOwners || []).filter((owner) => !owner.isPrimary));
        beneficialOwners.forEach((ele) => {
            if (account?.BeneficialOwners && account.BeneficialOwners.length > 1) {
                result.push(
                    <p>
                        <b>{`${label} ${ele.isPrimary ? "Primary Account Holder" : "Secondary Account Holder"}`}</b>
                    </p>
                );
            }
            if (ele.CustomerTaxResidencyModel && ele.CustomerTaxResidencyModel.length > 0) {
                //has CCM data
                result = result.concat(
                    ele.CustomerTaxResidencyModel.map((item) => {
                        this.loadMifirCountryIdentifier(item.Country);
                        return (
                            <>
                                <SelectEditor
                                    options={CountryCodeMap.getArray()}
                                    getText={(v) => {
                                        return CountryCodeMap.getString(v);
                                    }}
                                    placeholder="Please select"
                                    label={"Nationality"}
                                    selected={item?.Country}
                                    disabled
                                    cssWrapper="control-size-430"
                                    onChange={(v) => {}}
                                />
                                {item?.Country != CountryCode.GBR && (
                                    <NationalIdentifierPair
                                        data={item}
                                        onChange={(v) => {}}
                                        readonly
                                        onRef={(ref) => this.appendChild(ref)}
                                        mifirCountryIdentifier={
                                            this.state.mifirCountryIdentifiers.has(item.Country)
                                                ? this.state.mifirCountryIdentifiers.get(item.Country)
                                                : []
                                        }
                                    />
                                )}
                                {item?.Country == CountryCode.GBR && (
                                    <NINumberTextEditor
                                        label="National Insurance Number"
                                        value={item.TaxReference}
                                        onChange={(v) => {}}
                                        readonly={true}
                                    />
                                )}
                            </>
                        );
                    })
                );
            } else {
                const data = ele.isPrimary ? primary : secondary;
                const taxResidencyDetail = {
                    Country: data?.Country,
                    NationalIdentifier: data?.Type == MifirIdentifierType.NationalIdentifier ? data?.Value : null,
                    PassportNumber: data?.Type == MifirIdentifierType.PassportNumber ? data?.Value : null,
                };
                if (data?.Country && data?.Country !== CountryCode.GBR) {
                    this.loadMifirCountryIdentifier(data?.Country);
                }
                //mannually add
                result = result.concat(
                    <>
                        <SelectEditor
                            options={CountryCodeMap.getArray()}
                            getText={(v) => {
                                return CountryCodeMap.getString(v);
                            }}
                            placeholder="Please select"
                            label={"Nationality"}
                            selected={data?.Country}
                            required="Please select"
                            cssWrapper="control-size-430"
                            onChange={(v) => {
                                const key = `${ele.isPrimary ? "" : "Secondary"}Custom${
                                    direction == accountDirection.source ? "Source" : "Destination"
                                }NationalIdentifier`;
                                this.onChange({
                                    [key]: { ...data, Country: v, Type: MifirIdentifierType.NotSpecified, Value: null },
                                });
                            }}
                        />
                        {data?.Country && data?.Country != CountryCode.GBR && (
                            <NationalIdentifierPair
                                data={taxResidencyDetail}
                                onChange={(
                                    mifirIdentifierIdentifier: MifirIdentifierType,
                                    customerTaxResidencyModel: CustomerTaxResidencyModel
                                ) => {
                                    const key = `${ele.isPrimary ? "" : "Secondary"}Custom${
                                        direction == accountDirection.source ? "Source" : "Destination"
                                    }NationalIdentifier`;
                                    const identifier: NationalIdentifier = {
                                        Type: mifirIdentifierIdentifier,
                                        Value: this.getIdentiferReference(
                                            mifirIdentifierIdentifier,
                                            customerTaxResidencyModel
                                        ),
                                    };
                                    this.onChange({ [key]: { ...data, ...identifier } });
                                }}
                                onRef={(ref) => this.appendChild(ref)}
                                mifirCountryIdentifier={
                                    this.state.mifirCountryIdentifiers.has(data.Country)
                                        ? this.state.mifirCountryIdentifiers.get(data.Country)
                                        : []
                                }
                            />
                        )}
                        {data?.Country && data?.Country == CountryCode.GBR && (
                            <NINumberTextEditor
                                label="National Insurance Number"
                                value={data.Value}
                                onChange={(v) => {
                                    const key = `${ele.isPrimary ? "" : "Secondary"}Custom${
                                        direction == accountDirection.source ? "Source" : "Destination"
                                    }NationalIdentifier`;
                                    const identifierType = MifirIdentifierType.TaxNumber;
                                    const identifier: NationalIdentifier = {
                                        Type: identifierType,
                                        Value: v,
                                    };
                                    this.onChange({ [key]: { ...data, ...identifier } });
                                }}
                                readonly={false}
                            />
                        )}
                    </>
                );
            }
        });
        return result;
    }

    getIdentiferReference(
        mifirIdentifierIdentifier: MifirIdentifierType,
        customerTaxResidencyModel: CustomerTaxResidencyModel
    ): string {
        switch (mifirIdentifierIdentifier) {
            case MifirIdentifierType.NationalIdentifier:
                return customerTaxResidencyModel.NationalIdentifier;
            case MifirIdentifierType.PassportNumber:
                return customerTaxResidencyModel.PassportNumber;
            case MifirIdentifierType.TaxNumber:
                return customerTaxResidencyModel.TaxReference;
            default:
                throw new Error("Incorrect Mifir identifier type");
        }
    }
    render() {
        const { data, sourceAccount, destinationAccount } = this.props;
        return (
            <>
                <h3>Source Account</h3>
                {this.renderAccount(
                    sourceAccount,
                    accountDirection.source,
                    data.ChangeOfBeneficialOwner?.CustomSourceNationalIdentifier,
                    data.ChangeOfBeneficialOwner?.CustomSourceNationalIdentifier,
                    data.ChangeOfBeneficialOwner?.CustomSourceTrustLei,
                    data.ChangeOfBeneficialOwner?.CustomDestinationTrustLei
                )}
                <br />
                <h3>Destination Account</h3>
                {this.renderAccount(
                    destinationAccount,
                    accountDirection.destination,
                    data.ChangeOfBeneficialOwner?.CustomDestinationNationalIdentifier,
                    data.ChangeOfBeneficialOwner?.CustomDestinationNationalIdentifier,
                    data.ChangeOfBeneficialOwner?.CustomSourceTrustLei,
                    data.ChangeOfBeneficialOwner?.CustomDestinationTrustLei
                )}
            </>
        );
    }

    loadMifirCountryIdentifier(countryCode: CountryCode) {
        const { mifirCountryIdentifiers } = this.state;
        if (!mifirCountryIdentifiers.has(countryCode)) {
            new MifirCountryIdentifierClient().get(countryCode).onSuccess((data) => {
                mifirCountryIdentifiers.set(countryCode, data);
                this.setState({ mifirCountryIdentifiers: mifirCountryIdentifiers });
            });
        }
    }
}
